import { useBreakpointQuery } from '@ctw/shared/hooks/breakpoints';
import { type ClassName, tw, twx } from '@ctw/shared/utils/tailwind';
import { Transition } from '@headlessui/react';
import {
  type PropsWithChildren,
  type ReactNode,
  createContext,
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

export interface ListDetailViewWithDockableDrawerState {
  setHeaderContent: (content: ReactNode | null) => void;
  headerContent: ReactNode | null;
  setDetailPanelContent: (content: ReactNode | null) => void;
  detailPanelContent: ReactNode | null;
}

const ListDetailViewWithDockableDrawerContext =
  createContext<ListDetailViewWithDockableDrawerState | null>(null);

interface ListDetailViewWithDockableDrawerProviderProps {
  children: (state: ListDetailViewWithDockableDrawerState) => ReactNode;
}

const ListDetailViewWithDockableDrawerProvider = ({
  children,
}: ListDetailViewWithDockableDrawerProviderProps) => {
  const [headerContent, setHeaderContent] = useState<ReactNode | null>(null);
  const [detailPanelContent, setDetailPanelContent] = useState<ReactNode | null>(null);

  const state: ListDetailViewWithDockableDrawerState = useMemo(
    () => ({
      headerContent,
      setHeaderContent,
      detailPanelContent,
      setDetailPanelContent,
    }),
    [headerContent, detailPanelContent],
  );

  return (
    <ListDetailViewWithDockableDrawerContext.Provider value={state}>
      {children(state)}
    </ListDetailViewWithDockableDrawerContext.Provider>
  );
};

export interface ListDetailViewWithDockableDrawerProps extends PropsWithChildren {
  className?: ClassName;
}

/**
 * List-detail layout with an optional header and a detail panel that shows beside the list.
 * Use useListDetailViewHeader and useListDetailViewDetailPanel inside children to set content.
 */
export const ListDetailViewWithDockableDrawer = ({
  children,
  className,
}: ListDetailViewWithDockableDrawerProps) => {
  return (
    <ListDetailViewWithDockableDrawerProvider>
      {({ headerContent, detailPanelContent }) => (
        <ListDetailViewLayout
          className={className}
          headerContent={headerContent}
          detailPanelContent={detailPanelContent}
        >
          {children}
        </ListDetailViewLayout>
      )}
    </ListDetailViewWithDockableDrawerProvider>
  );
};

function ListDetailViewLayout({
  children,
  className,
  headerContent,
  detailPanelContent,
}: ListDetailViewWithDockableDrawerProps & {
  headerContent: ReactNode | null;
  detailPanelContent: ReactNode | null;
}) {
  const contentForFadeOut = useRef<ReactNode | null>(null);
  const [listDimmed, setListDimmed] = useState(false);
  const { breakpoints } = useBreakpointQuery();

  if (detailPanelContent !== null) {
    contentForFadeOut.current = detailPanelContent;
  }
  const panelContentToShow = detailPanelContent ?? contentForFadeOut.current;

  const handlePanelAfterLeave = () => {
    contentForFadeOut.current = null;
    setListDimmed(false);
  };

  const fadeOutTransition = () => {
    setListDimmed(true);
  };

  return (
    <div
      className={twx(
        'flex h-full w-full max-w-full flex-row gap-0 overflow-hidden md:gap-2',
        className,
      )}
    >
      <div
        className={twx(
          'flex min-w-0 flex-1 flex-col overflow-hidden rounded-md bg-white transition-all duration-300 ease-in-out',
          {
            'w-full': contentForFadeOut.current === null,
            'w-[0px] min-w-[0px] max-w-[0px]': contentForFadeOut.current !== null,
            'opacity-100': !listDimmed,
            'opacity-0': listDimmed,
            'w-[300px] min-w-[300px] max-w-[300px]':
              breakpoints.isAtLeast.lg && contentForFadeOut.current !== null,
            'w-[400px] min-w-[400px] max-w-[400px]':
              breakpoints.isAtLeast.xl && contentForFadeOut.current !== null,
          },
        )}
      >
        {headerContent && (
          <div className={tw`flex shrink-0 border-divider-main border-b p-2`}>{headerContent}</div>
        )}
        <div
          className={tw`flex min-h-0 flex-1 flex-col gap-0 overflow-auto transition-all duration-300 ease-in-out`}
        >
          {children}
        </div>
      </div>
      <Transition
        show={!!detailPanelContent}
        beforeLeave={fadeOutTransition}
        afterLeave={handlePanelAfterLeave}
      >
        <div
          className={twx(
            'flex min-w-0 flex-1 flex-col overflow-hidden rounded-md border-divider-main border-l bg-white',
            'transition-[transform,width,opacity] duration-300 ease-out',
            'data-[closed]:w-0 data-[closed]:translate-x-full data-[closed]:opacity-0',
            'data-[open]:w-full data-[open]:flex-1 data-[open]:translate-x-0 data-[open]:opacity-100',
            'data-[enter]:duration-300 data-[leave]:duration-300 data-[enter]:ease-out data-[leave]:ease-in',
          )}
        >
          {panelContentToShow}
        </div>
      </Transition>
    </div>
  );
}

export function useListDetailViewHeader(
  content: () => ReactNode,
  dependencies: Array<unknown>,
): void {
  const context = useContext(ListDetailViewWithDockableDrawerContext);

  // biome-ignore lint/correctness/useExhaustiveDependencies: controlled by caller
  useLayoutEffect(() => {
    context?.setHeaderContent(content());

    return () => {
      context?.setHeaderContent(null);
    };
  }, dependencies);
}

/**
 * Register detail panel content. When content is non-null it will be shown in the detail panel.
 * When content is null the panel will be hidden.
 */
export function useListDetailViewDetailPanel(
  content: ReactNode | (() => ReactNode),
  dependencies: Array<unknown>,
): void {
  const context = useContext(ListDetailViewWithDockableDrawerContext);

  // biome-ignore lint/correctness/useExhaustiveDependencies: controlled by caller
  useLayoutEffect(() => {
    const node = typeof content === 'function' ? content() : content;
    context?.setDetailPanelContent(node ?? null);

    return () => {
      context?.setDetailPanelContent(null);
    };
  }, dependencies);
}

/**
 * Returns whether the component is rendered inside a ListDetailViewWithDockableDrawer.
 * Use this to avoid double-wrapping when the layout already provides the list-detail view.
 */
export function useHasListDetailViewWithDockableDrawer(): boolean {
  const context = useContext(ListDetailViewWithDockableDrawerContext);
  return context !== null;
}
